/**
 * 수신허용 · 수신거부 목록에 주소 한 줄 등록 (migration 160, 2026-09-05).
 *
 * ★ **한 번에 하나다**(정본 P2-1 「목록을 담는 절」). 여러 줄을 한꺼번에 붙여 넣게 하면
 *   어느 줄이 왜 안 들어갔는지를 말할 자리가 사라진다.
 * ★ 오류는 **한 자리에만** 적는다 — 팝업이 열려 있으면 팝업 안에(메일함 탭에서 배운 것).
 */

import { useState } from 'react';
import { Button } from '../../components/shared/Button';
import { FormField } from '../../components/shared/FormField';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { tMail } from '../../lib/mail-i18n';
import type { MailAddressRuleKind } from '../../api/mail';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface MailAddressRuleModalProps {
  kind: MailAddressRuleKind;
  /** 제목을 직접 준다 — 자동전달도 「주소 한 줄 등록」이라 이 팝업을 나눠 쓴다(2026-09-05). */
  title?: string;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (address: string) => void;
}

export function MailAddressRuleModal({ kind, title, busy, error, onClose, onSubmit }: MailAddressRuleModalProps) {
  const [address, setAddress] = useState('');
  const [local, setLocal] = useState<string | null>(null);

  const submit = () => {
    const v = address.trim();
    if (!EMAIL_RE.test(v)) { setLocal(tMail('mail.spamset.err_addr')); return; }
    setLocal(null);
    onSubmit(v);
  };

  return (
    <Modal
      title={title ?? (kind === 'ALLOW' ? tMail('mail.spamset.add_allow_title') : tMail('mail.spamset.add_block_title'))}
      size="sm"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {tMail('mail.spamset.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {tMail('mail.spamset.register')}
          </Button>
        </ModalActions>
      )}
    >
      <div className="mboxmodal">
        <FormField label={tMail('mail.spamset.addr_label')} required>
          <Input
            type="email"
            value={address}
            maxLength={320}
            autoFocus
            placeholder={tMail('mail.spamset.addr_ph')}
            onChange={(e) => { setAddress(e.target.value); if (local) setLocal(null); }}
            /* ★ 한 줄 칸의 Enter 는 **여기서 막고 우리가 보낸다** — 안 막으면 폼이 통째로
               올라가 같은 주소가 여러 번 등록된다(P5 「Enter 가 폼을 올린다」에서 배운 것). */
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!busy) submit(); } }}
            fullWidth
          />
        </FormField>
        {(local ?? error) && <p className="mboxmodal__err" role="alert">{local ?? error}</p>}
      </div>
    </Modal>
  );
}
